import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ChevronRight, MapPin, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/DateRangePicker';
import { GeoFilters } from '@/components/GeoFilters';
import { ExportButton } from '@/components/ExportButton';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
}

export default function TerritoryPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ level: 'zone', data: [], kpis: {} });
  const [beats, setBeats] = useState([]);
  const [filters, setFilters] = useState({ zone: '', subzone: '', state: '', city: '', beat: '' });
  const [breadcrumb, setBreadcrumb] = useState([{ label: 'All Zones', filters: {} }]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadTerritory = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (f.zone) params.zone = f.zone;
      if (f.subzone) params.subzone = f.subzone;
      if (f.state) params.state = f.state;
      if (f.city) params.city = f.city;
      if (f.beat) params.beat = f.beat;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/territory', { params });
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load territory data');
    } finally {
      setLoading(false);
    }
  }, [api, filters, dateFrom, dateTo]);

  useEffect(() => { loadTerritory(); }, [loadTerritory]);

  // The beat list follows every place pick above it; the four place dropdowns narrow each other.
  useEffect(() => {
    const { zone, subzone, state, city } = filters;
    if (!(zone || subzone || state || city)) { setBeats([]); return; }
    let live = true;
    api.get('/geo/beats', { params: { zone, subzone, state, city } })
      .then(r => { if (live) setBeats(r.data); }).catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, filters.zone, filters.subzone, filters.state, filters.city]);

  // Merged from the previous value, because the bar can clear a narrower pick in the same breath
  // as it sets a wider one.
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'zone') { next.subzone = ''; next.city = ''; next.beat = ''; }
      if (key === 'subzone' || key === 'state') { next.city = ''; next.beat = ''; }
      if (key === 'city') { next.beat = ''; }
      return next;
    });
  };

  const setPlace = (key) => (v) => handleFilterChange(key, v);

  const drillDown = (item) => {
    const name = item.name;
    let newFilters = { ...filters };
    let newCrumb = { label: name, filters: { ...newFilters } };
    if (data.level === 'zone') { newFilters.zone = name; }
    else if (data.level === 'subzone') { newFilters.subzone = name; }
    else if (data.level === 'city') { newFilters.city = name; }
    else if (data.level === 'beat') { newFilters.beat = name; }
    else return;

    setBreadcrumb([...breadcrumb, newCrumb]);
    handleFilterChange(data.level === 'zone' ? 'zone' : data.level === 'subzone' ? 'subzone' : data.level === 'city' ? 'city' : 'beat', name);
  };

  const goBack = () => {
    if (breadcrumb.length <= 1) return;
    const prev = breadcrumb[breadcrumb.length - 2];
    setBreadcrumb(breadcrumb.slice(0, -1));
    setFilters(prev.filters || { zone: '', subzone: '', state: '', city: '', beat: '' });
  };

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="territory-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Territory</h1>
          <p className="text-sm text-muted-foreground mt-1">Geographic sales performance drill-down</p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <ExportButton module="territory" params={{ zone: filters.zone, subzone: filters.subzone,
            state: filters.state, city: filters.city, date_from: dateFrom, date_to: dateTo }} />
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm flex-wrap" data-testid="territory-breadcrumb">
        {breadcrumb.length > 1 && (
          <Button variant="ghost" size="sm" onClick={goBack} data-testid="territory-back-btn" className="h-7 px-2 mr-1">
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
        )}
        {breadcrumb.map((b, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight size={12} className="text-muted-foreground" />}
            <span className={i === breadcrumb.length - 1 ? 'font-medium text-foreground' : 'text-muted-foreground'}>{b.label}</span>
          </span>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-testid="territory-filters">
        <GeoFilters api={api} zone={filters.zone} setZone={setPlace('zone')}
          subzone={filters.subzone} setSubzone={setPlace('subzone')}
          state={filters.state} setState={setPlace('state')}
          city={filters.city} setCity={setPlace('city')} />
        {beats.length > 0 && (
          <Select value={filters.beat} onValueChange={v => handleFilterChange('beat', v)}>
            <SelectTrigger className="w-44" data-testid="filter-beat"><SelectValue placeholder="All Beats" /></SelectTrigger>
            <SelectContent>{beats.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Order Amount</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(k.total_order_amount || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Fulfilled</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(k.total_fulfilled_amount || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Fulfilment Rate</p>
          <p className="text-xl font-heading font-bold mt-1">{k.fulfilment_rate || 0}%</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Active Outlets</p>
          <p className="text-xl font-heading font-bold mt-1">{k.active_outlets?.toLocaleString('en-IN') || 0}</p>
        </div>
      </div>

      {/* Chart + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="font-heading font-semibold text-sm mb-4">Sales by {data.level === 'shop' ? 'Outlet' : data.level?.charAt(0).toUpperCase() + data.level?.slice(1)}</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.data?.slice(0, 15)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={120} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="order_amount" name="Order Amount" fill="#1B3A8C" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5 overflow-auto">
          <h3 className="font-heading font-semibold text-sm mb-4">Performance Table</h3>
          {loading ? <p className="text-muted-foreground text-sm">Loading...</p> : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-left text-xs">Name</TableHead>
                  <TableHead className="text-right text-xs">Orders</TableHead>
                  <TableHead className="text-right text-xs">Amount</TableHead>
                  <TableHead className="text-right text-xs">Outlets</TableHead>
                  <TableHead className="text-right text-xs">SKUs</TableHead>
                  {data.level !== 'shop' && <TableHead className="text-right text-xs"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data?.map((row, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => drillDown(row)}>
                    <TableCell className="font-medium text-sm flex items-center gap-1.5">
                      <MapPin size={12} className="text-muted-foreground" />
                      {row.name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-right text-sm">{row.total_orders?.toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-right text-sm">{formatINR(row.order_amount)}</TableCell>
                    <TableCell className="text-right text-sm">{row.active_outlets}</TableCell>
                    <TableCell className="text-right text-sm">{row.sku_count}</TableCell>
                    {data.level !== 'shop' && (
                      <TableCell className="text-right">
                        <ChevronRight size={14} className="text-muted-foreground" />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {(!data.data || data.data.length === 0) && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No data found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
